import React, { useEffect, useRef, useState } from 'react'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import { CSG } from 'three-csg-ts'

const ThreeScene: React.FC = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const scene = useRef(new THREE.Scene())
    const camera = useRef<THREE.PerspectiveCamera | null>(null)
    const renderer = useRef<THREE.WebGLRenderer | null>(null)
    const controls = useRef<OrbitControls | null>(null)
    const animationFrameId = useRef<number | null>(null)

    const raycaster = useRef(new THREE.Raycaster())
    const mouse = useRef(new THREE.Vector2())
    const draggable = useRef<THREE.Mesh | null>(null)
    const plane = useRef(new THREE.Plane())
    const planeIntersect = useRef(new THREE.Vector3())
    const offset = useRef(new THREE.Vector3())

    const [lastMovedMesh, setLastMovedMesh] = useState<THREE.Mesh | null>(null)
    const [selectedMeshes, setSelectedMeshes] = useState<THREE.Mesh[]>([])

    useEffect(() => {
        const canvas = canvasRef.current!

        scene.current.background = new THREE.Color(0xf0f0f0)
        camera.current = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000)
        camera.current.position.set(0, 2, 5)

        renderer.current = new THREE.WebGLRenderer({ canvas, antialias: true })
        renderer.current.setSize(window.innerWidth, window.innerHeight)

        controls.current = new OrbitControls(camera.current, canvas)

        scene.current.add(new THREE.AmbientLight(0xffffff, 0.6))
        const dirLight = new THREE.DirectionalLight(0xffffff, 0.8)
        dirLight.position.set(5, 10, 7.5)
        scene.current.add(dirLight)

        scene.current.add(new THREE.GridHelper(8, 8))
        const smallGrid = new THREE.GridHelper(8, 80)
        ;(smallGrid.material as THREE.Material).opacity = 0.2
        ;(smallGrid.material as THREE.Material).transparent = true
        scene.current.add(smallGrid)

        canvas.addEventListener('pointerdown', onPointerDown)
        canvas.addEventListener('pointermove', onPointerMove)
        canvas.addEventListener('pointerup', onPointerUp)
        window.addEventListener('resize', onWindowResize)

        animate()

        return () => {
            cancelAnimationFrame(animationFrameId.current!)
            renderer.current?.dispose()
            canvas.removeEventListener('pointerdown', onPointerDown)
            canvas.removeEventListener('pointermove', onPointerMove)
            canvas.removeEventListener('pointerup', onPointerUp)
            window.removeEventListener('resize', onWindowResize)
        }
    }, [])

    const animate = () => {
        animationFrameId.current = requestAnimationFrame(animate)
        controls.current?.update()
        renderer.current?.render(scene.current, camera.current!)
    }

    const onWindowResize = () => {
        camera.current!.aspect = window.innerWidth / window.innerHeight
        camera.current!.updateProjectionMatrix()
        renderer.current!.setSize(window.innerWidth, window.innerHeight)
    }

    const random = (min: number, max: number) => Math.random() * (max - min) + min

    const addCube = () => {
        const geometry = new THREE.BoxGeometry(1, 1, 1)
        const material = new THREE.MeshStandardMaterial({ color: 0x007bff })
        const cube = new THREE.Mesh(geometry, material)
        cube.position.set(random(-3, 3), 0.5, random(-3, 3))
        cube.userData.draggable = true
        scene.current.add(cube)
    }

    const addSphere = () => {
        const geometry = new THREE.SphereGeometry(0.5, 32, 32)
        const material = new THREE.MeshStandardMaterial({ color: 0x007bff })
        const sphere = new THREE.Mesh(geometry, material)
        sphere.position.set(random(-3, 3), 0.5, random(-3, 3))
        sphere.userData.draggable = true
        scene.current.add(sphere)
    }

    const getMouse = (event: PointerEvent) => {
        const rect = canvasRef.current!.getBoundingClientRect()
        mouse.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
        mouse.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1
    }

    const onPointerDown = (event: PointerEvent) => {
        getMouse(event)
        raycaster.current.setFromCamera(mouse.current, camera.current!)
        const intersects = raycaster.current.intersectObjects(scene.current.children, false)

        const intersect = intersects.find(obj => obj.object.userData.draggable)
        if (!intersect) {
            if (!event.shiftKey) clearSelection()
            return
        }

        const mesh = intersect.object as THREE.Mesh

        if (event.shiftKey) {
            toggleSelection(mesh)
        } else {
            if (selectedMeshes.includes(mesh)) {
                startDrag(mesh)
            } else {
                clearSelection()
                selectMesh(mesh)
                startDrag(mesh)
            }
        }
    }

    const startDrag = (mesh: THREE.Mesh) => {
        draggable.current = mesh
        plane.current.set(new THREE.Vector3(0, 1, 0), -mesh.position.y)
        if (raycaster.current.ray.intersectPlane(plane.current, planeIntersect.current)) {
            offset.current.copy(planeIntersect.current).sub(mesh.position)
        }
        controls.current!.enabled = false
        canvasRef.current!.classList.add('dragging')
    }

    const onPointerMove = (event: PointerEvent) => {
        getMouse(event)
        raycaster.current.setFromCamera(mouse.current, camera.current!)
        const hovering = raycaster.current.intersectObjects(scene.current.children, false).find(i => i.object.userData.draggable)

        if (hovering && !draggable.current) {
            canvasRef.current!.classList.add('hover-draggable')
        } else {
            canvasRef.current!.classList.remove('hover-draggable')
        }

        if (draggable.current && raycaster.current.ray.intersectPlane(plane.current, planeIntersect.current)) {
            const y = draggable.current.position.y
            const newPos = planeIntersect.current.clone().sub(offset.current)
            newPos.y = y
            const snapped = snapToGrid(newPos, 0.1)
            snapped.x = THREE.MathUtils.clamp(snapped.x, -15, 15)
            snapped.z = THREE.MathUtils.clamp(snapped.z, -15, 15)
            snapped.y = THREE.MathUtils.clamp(snapped.y, 0, 30)
            draggable.current.position.copy(snapped)
        }
    }

    const onPointerUp = () => {
        controls.current!.enabled = true
        canvasRef.current!.classList.remove('dragging')
        if (draggable.current) {
            setLastMovedMesh(draggable.current)
            draggable.current = null
        }
    }

    const snapToGrid = (v: THREE.Vector3, size = 1) => new THREE.Vector3(
        Math.round(v.x / size) * size,
        v.y,
        Math.round(v.z / size) * size
    )

    const toggleSelection = (mesh: THREE.Mesh) => {
        setSelectedMeshes(prev => {
            const exists = prev.includes(mesh)
            const newSelection = exists ? prev.filter(m => m !== mesh) : [...prev, mesh]
            updateHighlight(newSelection)
            return newSelection
        })
    }

    const selectMesh = (mesh: THREE.Mesh) => {
        setSelectedMeshes(prev => {
            const newSelection = [...prev, mesh]
            updateHighlight(newSelection)
            return newSelection
        })
    }

    const clearSelection = () => {
        updateHighlight([])
        setSelectedMeshes([])
    }

    const updateHighlight = (selection: THREE.Mesh[]) => {
        scene.current.traverse(child => {
            if ((child as THREE.Mesh).isMesh && child.userData.draggable) {
                const mesh = child as THREE.Mesh
                const mat = mesh.material as THREE.MeshStandardMaterial
                mat.emissive = mat.emissive || new THREE.Color(0x000000)
                mat.emissive.setHex(selection.includes(mesh) ? 0x4444ff : 0x000000)
            }
        })
    }

    const disposeMesh = (obj: THREE.Object3D) => {
        if (obj.parent) obj.parent.remove(obj)
        if (scene.current.children.includes(obj)) scene.current.remove(obj)
        const mesh = obj as THREE.Mesh
        mesh.geometry?.dispose()
        if (Array.isArray(mesh.material)) {
            mesh.material.forEach(m => m.dispose?.())
        } else {
            mesh.material?.dispose?.()
        }
        obj.children?.forEach(disposeMesh)
    }

    function getClonedMaterial(material: THREE.Material | THREE.Material[]): THREE.Material {
        const baseMaterial = Array.isArray(material) ? material[0] : material
        return baseMaterial.clone()
    }

    const subtractSelected = () => {
        if (!lastMovedMesh) return

        const boxMoved = new THREE.Box3().setFromObject(lastMovedMesh)

        for (const obj of [...scene.current.children]) {
            if (obj === lastMovedMesh || !(obj as THREE.Mesh).isMesh) continue

            const boxObj = new THREE.Box3().setFromObject(obj)
            if (!boxObj.intersectsBox(boxMoved)) continue

            const meshObj = obj as THREE.Mesh
            const csgObj = CSG.fromMesh(meshObj)
            const csgMoved = CSG.fromMesh(lastMovedMesh)
            const result = csgObj.subtract(csgMoved)

            const material = getClonedMaterial(meshObj.material)
            const mesh = CSG.toMesh(result, meshObj.matrix, material)
            mesh.userData.draggable = true

            disposeMesh(meshObj)
            disposeMesh(lastMovedMesh)
            scene.current.add(mesh)

            setLastMovedMesh(null)
            return
        }

        disposeMesh(lastMovedMesh)
        setLastMovedMesh(null)
    }


    const combineSelected = () => {
        if (selectedMeshes.length < 2) return

        let result = CSG.fromMesh(selectedMeshes[0])
        for (let i = 1; i < selectedMeshes.length; i++) {
            result = result.union(CSG.fromMesh(selectedMeshes[i]))
        }

        const base = selectedMeshes[0]
        const material = getClonedMaterial(base.material)
        const mesh = CSG.toMesh(result, base.matrix, material)
        mesh.userData.draggable = true

        selectedMeshes.forEach(disposeMesh)
        scene.current.add(mesh)
        setSelectedMeshes([])
        updateHighlight([])
        setLastMovedMesh(mesh)
    }

    return (
        <div className="relative w-full h-screen">
            <canvas ref={canvasRef} className="w-full h-full cursor-default" />

            <div className="absolute top-4 left-4 space-x-2 z-10 flex bg-white bg-opacity-80 p-4 rounded shadow">
                <button onClick={addCube} className="px-4 py-2 bg-blue-500 text-white rounded hover:scale-105 transition">
                    Add Cube
                </button>
                <button onClick={addSphere} className="px-4 py-2 bg-green-500 text-white rounded hover:scale-105 transition">
                    Add Sphere
                </button>
                <button
                    onClick={subtractSelected}
                    disabled={!lastMovedMesh}
                    className={`px-4 py-2 rounded transition ${
                        !lastMovedMesh ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600 hover:scale-105 text-white'
                    }`}
                >
                    Subtract
                </button>
                <button
                    onClick={combineSelected}
                    disabled={selectedMeshes.length < 2}
                    className={`px-4 py-2 rounded transition ${
                        selectedMeshes.length < 2 ? 'bg-gray-400 cursor-not-allowed' : 'bg-purple-600 hover:scale-105 text-white'
                    }`}
                >
                    Combine
                </button>
            </div>
        </div>
    )
}

export default ThreeScene
